import { fetchActiveChains } from "@/API/offers";

const getDefaultState = () => {
  return {
    shopChosenPackage: null,
    lobbyOffer: null,
    specialOffer: null,
    offerId: "",
    autoShowSpecialOffer: true,
    redirectResult: null,
    sessionId: null,
    transactionId: null,
    paymentCompleted: false,
    paymentError: false,
    paymentStatus: "",
    adyenData: null,
    paymentMethod: "",
    // Global offer state (v2 API)
    globalOfferData: null,
    showGlobalOfferModal: false,
    // True when the offer was reopened manually from the menu-event badge.
    // Lets the App.vue modal bypass the auto-show pipeline gates so a user-
    // initiated reopen always works (the auto-show path keeps the gates).
    offerManualOpen: false,
    // Offer-chain state (v2 API)
    // All active chain wrappers (drives the per-chain badges in the menu).
    offerChains: [],
    // The chain wrapper currently shown in the modal (selected via its badge).
    offerChainData: null,
    showOfferChainModal: false,
    // Transient: set true before a post-purchase chain reopen so the layout
    // plays the lock→unlock (fly-out → fly-in) transition on the newly-current
    // tile. Cleared once played / on clearOfferChainData.
    offerChainUnlockAnim: false,
  };
};
const state = getDefaultState();

const mutations = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },

  setShopChosenPackage(state, payload) {
    state.shopChosenPackage = payload;
  },
  setLobbyOffer(state, payload) {
    state.lobbyOffer = payload;
  },
  setSpecialOffer(state, payload) {
    state.specialOffer = payload;
  },
  setPaymentMethod: (state, string) => (state.paymentMethod = string),
  setPaymentCompleted: (state, boolean) => (state.paymentCompleted = boolean),
  setPaymentError: (state, boolean) => (state.paymentError = boolean),
  setPaymentStatus: (state, string) => (state.paymentStatus = string),
  setTransactionId: (state, number) => (state.transactionId = number),
  setAdyenData: (state, payload) => (state.adyenData = payload),
  setOfferId: (state, string) => (state.offerId = string),
  setAutoShowSpecialOffer: (state, boolean) =>
    (state.autoShowSpecialOffer = boolean),
  setRedirectResult: (state, payload) => (state.redirectResult = payload),
  setSessionID: (state, payload) => (state.sessionId = payload),
  clearLastPayment: (state) => {
    state.redirectResult = null;
    state.sessionId = null;
    state.transactionId = null;
    state.shopChosenPackage = null;
    state.adyenData = null;
    state.paymentCompleted = false;
    state.paymentError = false;
    state.paymentStatus = "";
  },
  // Global offer mutations (v2 API)
  setGlobalOfferData: (state, payload) => {
    // The lobby/level offer trigger also returns the active offer-chain
    // (is_chain / display_type "chain"). Chains have their own pipeline
    // (offerChainData + modal-offer-chain), so ignore them here to avoid a
    // duplicate normal-offer badge/modal alongside the chain badge.
    const isChain =
      !!payload &&
      (payload.is_chain === true || payload.display_type === "chain");
    if (isChain) {
      state.globalOfferData = null;
      state.showGlobalOfferModal = false;
      return;
    }
    state.globalOfferData = payload;
    // Auto-show modal when new offer data is set
    // state.showGlobalOfferModal = true;
  },
  clearGlobalOfferData: (state) => {
    state.globalOfferData = null;
    state.showGlobalOfferModal = false;
    state.offerManualOpen = false;
  },
  setShowGlobalOfferModal: (state, value) =>
    (state.showGlobalOfferModal = value),
  setOfferManualOpen: (state, value) => (state.offerManualOpen = value),
  // Offer-chain mutations (v2 API)
  // All active chains (per-chain badges).
  setOfferChains: (state, payload) => {
    state.offerChains = Array.isArray(payload) ? payload : [];
  },
  // Drop a single chain (e.g. its badge countdown expired). If it was the one
  // open in the modal, also clear the selection + hide the modal.
  removeOfferChain: (state, chainId) => {
    state.offerChains = state.offerChains.filter((c) => c && c.id !== chainId);
    if (state.offerChainData && state.offerChainData.id === chainId) {
      state.offerChainData = null;
      state.showOfferChainModal = false;
      state.offerChainUnlockAnim = false;
    }
  },
  // Select which chain the modal shows.
  setOfferChainData: (state, payload) => {
    state.offerChainData = payload;
  },
  clearOfferChainData: (state) => {
    state.offerChains = [];
    state.offerChainData = null;
    state.showOfferChainModal = false;
    state.offerChainUnlockAnim = false;
  },
  setShowOfferChainModal: (state, value) => (state.showOfferChainModal = value),
  setOfferChainUnlockAnim: (state, value) =>
    (state.offerChainUnlockAnim = value),
  // Clear all offer state on app init - should not persist between sessions
  initGlobalOfferData: (state, useV2Offers) => {
    state.globalOfferData = null;
    state.showGlobalOfferModal = false;
    state.offerManualOpen = false;
    state.offerChains = [];
    state.offerChainData = null;
    state.showOfferChainModal = false;
    // Also clear legacy offer state to prevent stale offers from rendering
    // When v2 is disabled, also clear legacy offer state
    if (!useV2Offers) {
      state.lobbyOffer = null;
      state.specialOffer = null;
      state.offerId = "";
    }
  },
};

const getters = {
  getShopChosenPackage: (state) => state.shopChosenPackage,
  getLobbyOffer: (state) => state.lobbyOffer,
  getSpecialOffer: (state) => state.specialOffer,
  getSpecialOfferTime: (state) => {
    if (!state.specialOffer) return null;
    let time = new Date(state.specialOffer.valid_until).getTime();
    return time;
  },
  getShowBuyResult: (state) =>
    state.transactionId && state.sessionId && state.redirectResult,
  // Global offer getters (v2 API)
  getGlobalOfferData: (state) => state.globalOfferData,
  getShowGlobalOfferModal: (state) => state.showGlobalOfferModal,
  getOfferManualOpen: (state) => state.offerManualOpen,
  // Offer-chain getters (v2 API)
  getOfferChains: (state) => state.offerChains,
  getOfferChainData: (state) => state.offerChainData,
  getShowOfferChainModal: (state) => state.showOfferChainModal,
};
const actions = {
  resetShopState({ commit }) {
    commit("resetState");
  },
  // Fetch all active offer-chains (drives the per-chain badges). Each wrapper
  // holds runtime fields + `.offer`. If a chain is currently selected (open in
  // the modal), re-sync it by id so its refreshed states/progress flow through
  // after a purchase. Returns the active-chain array.
  async fetchOfferChain({ commit, state }) {
    try {
      const chains = await fetchActiveChains();
      const active = Array.isArray(chains)
        ? chains.filter((c) => c && c.is_active)
        : [];
      commit("setOfferChains", active);
      // Keep the modal pointed at the same chain after a refetch (or drop it if
      // it's no longer active).
      const selectedId = state.offerChainData && state.offerChainData.id;
      if (selectedId != null) {
        const still = active.find((c) => c.id === selectedId);
        if (still) commit("setOfferChainData", still);
      }
      return active;
    } catch (error) {
      console.error("[OfferChain] fetchOfferChain failed:", error);
      commit("clearOfferChainData");
      return [];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
